import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  NestedMultiselectListOption,
  NestedMultiselectListSelectedValue,
} from 'shared/components'

interface LockListOptionSource {
  node: {
    id: string
    name: string
    description?: string | null
    location?: {
      name?: string | null
    } | null
  }
  options?: {
    nodes: {
      id: string
      title: string
      displayValue?: string | null
      sortOrder?: number
    }[]
  } | null
}

export const mapAccessToLockListSelectedValue = ({
  node: { id },
  options,
}: {
  node: { id: string }
  options?: {
    nodes: { id: string }[]
  } | null
}): NestedMultiselectListSelectedValue<string> => ({
  value: id,
  subItems: options?.nodes.map(option => option.id) ?? [],
})

export const useMapAccessToLockListOption = (showLocation = false) => {
  const { t } = useTranslation()

  return useCallback(
    ({
      node: { id, name, description, location },
      options,
    }: LockListOptionSource): NestedMultiselectListOption<string, string> => {
      const locationName = showLocation ? location?.name : undefined

      return {
        value: id,
        title: name,
        subtitle:
          description && locationName ? (
            <>
              {description}
              <br />
              {locationName}
            </>
          ) : (
            description || locationName || undefined
          ),
        subItems: [...(options?.nodes ?? [])]
          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
          .map(({ id: optionId, title, displayValue }) => {
            const parsedTitle = t(`enums.lockOptions.${title}`)

            return {
              value: optionId,
              title: displayValue
                ? `${parsedTitle}: ${displayValue}`
                : parsedTitle,
            }
          }),
      }
    },
    [t, showLocation]
  )
}
